import { apiClient } from './apiClient';

export function getPatientProductTypes(
  patientId: string,
  params?: { availableDate?: string },
) {
  return apiClient
    .get(`/patients/${patientId}/product-types`, { params })
    .then((r) => r.data);
}

export function consumeProduct(
  patientId: string,
  body: { productId: number; type: string; quantity?: number },
) {
  return apiClient
    .post(`/patients/${patientId}/product-types/consume`, body)
    .then((r) => r.data);
}

export function deleteProduct(
  patientId: string,
  body: { productId: number; type: string; quantity?: number },
) {
  return apiClient
    .post(`/patients/${patientId}/product-types/delete-product`, body)
    .then((r) => r.data);
}

export function returnProduct(
  patientId: string,
  body: { productId: number; type: string; scheduledAppointmentProductsID?: number },
) {
  return apiClient
    .post(`/patients/${patientId}/product-types/return`, body)
    .then((r) => r.data);
}

export function updateProductExpireDate(
  patientId: string,
  body: { productId: number; type: string; expireDate: string; quantity?: number },
) {
  return apiClient
    .post(`/patients/${patientId}/product-types/update-expire-date`, body)
    .then((r) => r.data);
}

// ── Appointment product endpoints ──

export interface AppointmentProductsParams {
  appointmentTypeID?: string;
  apptID?: string;
  apptStartDate?: string;
  apptDate?: string;
  facilityID?: string;
  apptComp?: string;
  /** Which side of the required set to preview: the products or the single equivalent product. */
  productMode?: "products" | "credit";
}

/** One labelled option for the required-set switch (product name + quantity). */
export interface RequiredOption {
  productTypeID: number;
  productTypeName: string;
  quantity: number;
  /** False when this option has no active cost item (unpriced → not selectable). */
  available?: boolean;
}

export type ProductMode = "products" | "credit";

export interface ApptProductSlot {
  scheduledAppointmentProductsID: string;
  appointmentProductAddonsID: string;
  productName: string;
  quantity: number;
  type: string;
  quoteItemCostID: string;
  paymentMaterialID: string;
  membershipProductID: string;
  packageProductID: string;
  addedDate: string;
}

export interface ApptProductType {
  productTypeID: string | number;
  productTypeName: string;
  productID: number;
  curRate: number;
  quantity: number;
  addonsQuantity: number;
  /** False when this product type has no active cost item (cannot be purchased). */
  hasCostItem?: boolean;
  pending: ApptProductSlot[];
  pendingAvailable: ApptProductSlot[];
  disbursed: ApptProductSlot[];
  purchaseOthers: ApptProductSlot[];
}

export interface AppointmentProductsResponse {
  products: Record<string, ApptProductType>;
  showProductButtons: boolean;
  totalQuantity: number;
  totalDisbursed: number;
  isMember: boolean;
  compAppointment: boolean;
  patientInventory?: Record<string, unknown>;
  /** Required-set switch: which set is currently applied. */
  productMode?: ProductMode;
  /** True when the appointment type *defines* required products (regardless of pricing). */
  hasRequiredProducts?: boolean;
  /** True when the appointment type *defines* a single equivalent product (regardless of pricing). */
  hasServiceCredit?: boolean;
  /** False when part of the current set is disbursed (switch is locked). */
  canSwitch?: boolean;
  /** The required-products option, labelled for display. */
  requiredProductsOption?: RequiredOption[];
  /** The single equivalent product option, labelled for display. */
  serviceCreditOption?: RequiredOption | null;
  /** True when the currently-attached required set includes an unpriced product type. */
  attachedInvalid?: boolean;
}

export function getAppointmentProducts(
  patientId: string,
  params: AppointmentProductsParams,
) {
  return apiClient
    .get<{ data: AppointmentProductsResponse }>(
      `/patients/${patientId}/product-types/appointment-products`,
      { params },
    )
    .then((r) => r.data);
}

export function consumeAppointmentProduct(
  patientId: string,
  body: { apptID: string; productJson: ApptProductSlot[]; quantity?: number },
) {
  return apiClient
    .post(`/patients/${patientId}/product-types/appointment-consume`, body)
    .then((r) => r.data);
}

export function returnAppointmentProduct(
  patientId: string,
  body: {
    apptID: string;
    productJson: ApptProductSlot[];
    quantity?: number;
    type?: string;
  },
) {
  return apiClient
    .post(`/patients/${patientId}/product-types/appointment-return`, body)
    .then((r) => r.data);
}

export function compAppointmentProduct(
  patientId: string,
  body: {
    apptID: string;
    type?: string;
    productJson?: ApptProductSlot[];
    quantity?: number;
  },
) {
  return apiClient
    .post(`/patients/${patientId}/product-types/appointment-comp`, body)
    .then((r) => r.data);
}

export function removeAppointmentProduct(
  patientId: string,
  body: { apptID: string; filtersArray?: Record<string, string> },
) {
  return apiClient
    .post(`/patients/${patientId}/product-types/appointment-remove`, body)
    .then((r) => r.data);
}

export function addAppointmentProducts(
  patientId: string,
  body: { scheduledAppointmentsID: string },
) {
  return apiClient
    .post(
      `/patients/${patientId}/product-types/appointment-add-products`,
      body,
    )
    .then((r) => r.data);
}

export function saveAppointmentAddons(
  patientId: string,
  body: {
    scheduledAppointmentsID: string;
    productTypeID: string;
    productQuantity: number;
  },
) {
  return apiClient
    .post(`/patients/${patientId}/product-types/appointment-addons`, body)
    .then((r) => r.data);
}
